/**
 * Feature Gating Service
 * This service provides methods to check if a user can access specific features
 * based on their subscription plan
 */

import { doc, getDoc, collection, query, where, getDocs, addDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import SubscriptionPlansConfig from '../config/subscriptionPlansConfig';

class FeatureGatingService {
  constructor() {
    this._cachedSubscriptionCategory = null;
    this._cacheTimestamp = null;
    this._cacheValidityDuration = 5 * 60 * 1000; // 5 minutes in milliseconds
    this._cachedUserOverrides = null;
    this._overridesCacheTimestamp = null;
    console.log('✅ FeatureGatingService v2.3 LOADED - with tier limit checking + admin overrides');
  }

  /**
   * Check if the current user is a client
   */
  _isClientUser() {
    try {
      return localStorage.getItem("userType") === "client";
    } catch {
      return false;
    }
  }

  /**
   * Force-refresh the overrides cache. Call this if admin might have updated settings.
   */
  invalidateOverridesCache() {
    this._cachedUserOverrides = null;
    this._overridesCacheTimestamp = null;
  }

  /**
   * Fetch user-level gating overrides set by admin
   * Cached for 5 minutes to avoid excessive Firestore reads
   */
  async _getUserOverrides(userId) {
    const now = new Date();
    const cacheAge = this._overridesCacheTimestamp
      ? now.getTime() - this._overridesCacheTimestamp.getTime()
      : Infinity;

    if (
      this._overridesCacheTimestamp &&
      cacheAge < this._cacheValidityDuration &&
      this._cachedUserOverrides !== undefined
    ) {
      return this._cachedUserOverrides;
    }

    try {
      const overrideDoc = await getDoc(doc(db, 'userOverrides', userId));
      this._cachedUserOverrides = overrideDoc.exists() ? overrideDoc.data() : null;
      this._overridesCacheTimestamp = new Date();
      if (this._cachedUserOverrides) {
        console.log('📦 User overrides loaded from admin:', this._cachedUserOverrides);
      }
      return this._cachedUserOverrides;
    } catch (error) {
      console.error('Error fetching user overrides:', error);
      return null;
    }
  }

  /**
   * Extract a sortable value from Firestore document ID
   * For legacy cases without timestamps, use doc ID for consistent sorting
   */
  extractTimestampFromDocId(docId) {
    if (!docId) return 0;

    // Convert first 8 characters of doc ID to a numeric value for sorting
    // This ensures consistent ordering for legacy cases
    let hash = 0;
    const str = docId.substring(0, 8);
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash = hash & hash; // Convert to 32bit integer
    }

    // Return a small value that's much older than any real timestamp
    // Real timestamps are ~1700000000000 (billions), so using millions ensures legacy cases sort first
    return Math.abs(hash); // Will be in the millions, sorting before real timestamps
  }

  /**
   * Initialize and cache subscription details
   */
  async initialize(userId) {
    try {
      await Promise.all([
        this.refreshSubscriptionCache(userId),
        this._getUserOverrides(userId),
      ]);
      console.log('✅ Feature Gating Service initialized successfully');
    } catch (e) {
      console.error('❌ Error initializing Feature Gating Service:', e);
    }
  }

  /**
   * Refresh the cached subscription category
   */
  async refreshSubscriptionCache(userId) {
    try {
      const category = await this._fetchActivePlanCategory(userId);
      this._cachedSubscriptionCategory = category;
      this._cacheTimestamp = new Date();
      console.log('📦 Subscription cache refreshed:', category);
    } catch (e) {
      console.error('❌ Error refreshing subscription cache:', e);
      this._cachedSubscriptionCategory = null;
      this._cacheTimestamp = null;
    }
  }

  /**
   * Get the cached subscription category, refresh if needed
   */
  async _getSubscriptionCategory(userId) {
    const now = new Date();
    const cacheAge = this._cacheTimestamp
      ? now.getTime() - this._cacheTimestamp.getTime()
      : Infinity;

    // Check if cache is valid
    if (
      this._cacheTimestamp &&
      cacheAge < this._cacheValidityDuration &&
      this._cachedSubscriptionCategory !== null
    ) {
      return this._cachedSubscriptionCategory;
    }

    // Refresh cache if expired or null
    await this.refreshSubscriptionCache(userId);
    return this._cachedSubscriptionCategory;
  }

  /**
   * Fetch active plan category from Firebase
   */
  async _fetchActivePlanCategory(userId) {
    try {
      const subscriptionDoc = await getDoc(doc(db, 'subscriptions', userId));

      if (!subscriptionDoc.exists()) {
        return null; // Free plan (no subscription)
      }

      const data = subscriptionDoc.data();
      if (!data.transactions || data.transactions.length === 0) {
        return null;
      }

      // Find the latest active transaction
      let latestEndDate = null;
      let latestCategory = null;

      for (const transaction of data.transactions) {
        const endDate = transaction['end-time']?.toDate
          ? transaction['end-time'].toDate()
          : new Date(transaction['end-time']);

        // Only consider active subscriptions
        if (endDate > new Date()) {
          if (!latestEndDate || endDate > latestEndDate) {
            latestEndDate = endDate;
            latestCategory = transaction.category || null;
          }
        }
      }

      return latestCategory;
    } catch (error) {
      console.error('Error fetching active plan category:', error);
      return null;
    }
  }

  // ==========================================
  // CASE IMPORT/EXPORT LIMITS
  // ==========================================

  /**
   * Get maximum number of PENDING cases user can have
   * Note: This limit applies only to pending cases, not disposed cases
   */
  async getMaxCaseExportLimit(userId) {
    // Check admin overrides first
    const overrides = await this._getUserOverrides(userId);
    if (overrides && overrides.caseLimits !== undefined) {
      return overrides.caseLimits === -1 ? 999999 : overrides.caseLimits;
    }

    const category = await this._getSubscriptionCategory(userId);
    return SubscriptionPlansConfig.getCaseLimitByTier(category, this._isClientUser());
  }

  /**
   * Get maximum number of DISPOSED cases user can have
   * Note: Disposed cases have the same limits as pending cases
   */
  async getMaxDisposedCaseLimit(userId) {
    // Check admin overrides first
    const overrides = await this._getUserOverrides(userId);
    if (overrides && overrides.disposedCaseLimits !== undefined) {
      return overrides.disposedCaseLimits === -1 ? 999999 : overrides.disposedCaseLimits;
    }

    const category = await this._getSubscriptionCategory(userId);
    return SubscriptionPlansConfig.getCaseLimitByTier(category, this._isClientUser());
  }

  /**
   * Check if user can export more cases
   * Returns true if user is within limit
   */
  async canExportCases(userId, currentExportCount) {
    const maxLimit = await this.getMaxCaseExportLimit(userId);
    return currentExportCount < maxLimit;
  }

  /**
   * Get remaining case export slots
   */
  async getRemainingCaseExports(userId, currentExportCount) {
    const maxLimit = await this.getMaxCaseExportLimit(userId);
    const remaining = maxLimit - currentExportCount;
    return remaining > 0 ? remaining : 0;
  }

  /**
   * Check if user can dispose more cases
   * Returns true if user is within disposed case limit
   */
  async canDisposeMoreCases(userId, currentDisposedCount) {
    const maxLimit = await this.getMaxDisposedCaseLimit(userId);
    return currentDisposedCount < maxLimit;
  }

  /**
   * Get remaining disposed case slots
   */
  async getRemainingDisposedSlots(userId, currentDisposedCount) {
    const maxLimit = await this.getMaxDisposedCaseLimit(userId);
    const remaining = maxLimit - currentDisposedCount;
    return remaining > 0 ? remaining : 0;
  }

  // ==========================================
  // CASE SHARING / MATES LIMITS
  // ==========================================

  /**
   * Get maximum number of mates user can share cases with
   */
  async getMaxMatesSharingLimit(userId) {
    // Check admin overrides first
    const overrides = await this._getUserOverrides(userId);
    if (overrides && overrides.matesSharingLimits !== undefined) {
      return overrides.matesSharingLimits === -1 ? 999999 : overrides.matesSharingLimits;
    }

    const category = await this._getSubscriptionCategory(userId);

    switch (category) {
      case 'Lawyer-Basic':
        return 5;
      case 'Lawyer-Premium':
        return 10;
      case 'Lawyer-Enterprise':
        return 999999; // Unlimited
      default: {
        // Free plan - check if active or expired
        const subscription = await this.getActiveSubscriptionDetails(userId);
        const isActive = subscription && subscription.isActive;
        return isActive ? 10 : 5; // 10 mates during free plan, 5 after expiry
      }
    }
  }

  /**
   * Check if user can share with more mates
   * Returns true if user is within limit
   */
  async canShareWithMates(userId, currentMatesCount) {
    const maxLimit = await this.getMaxMatesSharingLimit(userId);
    return currentMatesCount < maxLimit;
  }

  /**
   * Get remaining mate sharing slots
   */
  async getRemainingMatesSlots(userId, currentMatesCount) {
    const maxLimit = await this.getMaxMatesSharingLimit(userId);
    const remaining = maxLimit - currentMatesCount;
    return remaining > 0 ? remaining : 0;
  }

  // ==========================================
  // FEATURE ACCESS CHECKS
  // ==========================================

  /**
   * Resolve the portal user type (lawyer | client | lawStudent) from localStorage.
   */
  _getUserType() {
    try {
      const t = localStorage.getItem('userType');
      if (t === 'client') return 'client';
      const cat = localStorage.getItem('userCategory') || localStorage.getItem('userCateogry');
      if (cat === 'Law student' || cat === 'lawStudent') return 'lawStudent';
      return 'lawyers';
    } catch {
      return 'lawyers';
    }
  }

  /**
   * Generic feature-flag check. Resolution order:
   *   1) per-user admin override (userOverrides/{uid}.features[flagKey])
   *   2) active plan's featureFlags[flagKey] (from adminConfig/subscriptionPlans)
   *   3) fallback (argument, default true so new/unseeded flags don't hard-lock)
   */
  async hasFeature(userId, flagKey, fallback = true) {
    try {
      if (!userId || !flagKey) return fallback;
      const overrides = await this._getUserOverrides(userId);
      const ov = overrides?.features?.[flagKey];
      if (typeof ov === 'boolean') return ov;

      const category = await this._getSubscriptionCategory(userId);
      const flags = SubscriptionPlansConfig.getFeatureFlagsByCategory(category, this._getUserType());
      if (flags && typeof flags[flagKey] === 'boolean') return flags[flagKey];

      return fallback;
    } catch (error) {
      console.error(`Error checking feature "${flagKey}":`, error);
      return fallback;
    }
  }

  /**
   * Return the fully merged flag map for the user: plan defaults ← admin overrides.
   */
  async getAllFeatureFlags(userId) {
    try {
      const category = await this._getSubscriptionCategory(userId);
      const planFlags = SubscriptionPlansConfig.getFeatureFlagsByCategory(category, this._getUserType()) || {};
      const overrides = await this._getUserOverrides(userId);
      const overrideFlags = overrides?.features || {};
      return { ...planFlags, ...overrideFlags };
    } catch (error) {
      console.error('Error loading feature flags:', error);
      return {};
    }
  }

  /**
   * Check if user has access to AI Chatbot
   */
  async canAccessAIChatbot(userId) {
    // Check admin overrides first
    const overrides = await this._getUserOverrides(userId);
    if (overrides?.features?.aiChatbot !== undefined) {
      return overrides.features.aiChatbot;
    }

    // Check plan's featureFlags (set from admin Subscriptions & Plans page)
    const category = await this._getSubscriptionCategory(userId);
    const planFlags = SubscriptionPlansConfig.getFeatureFlagsByCategory(category, this._getUserType());
    if (typeof planFlags?.aiChatbot === 'boolean') return planFlags.aiChatbot;

    // Legacy fallback — paid tiers only
    return (
      category === 'Lawyer-Basic' ||
      category === 'Lawyer-Premium' ||
      category === 'Lawyer-Enterprise'
    );
  }

  /**
   * Check if user has access to Client Connect
   */
  async canAccessClientConnect(userId) {
    // Check admin overrides first
    const overrides = await this._getUserOverrides(userId);
    if (overrides?.features?.clientConnect !== undefined) {
      return overrides.features.clientConnect;
    }

    const category = await this._getSubscriptionCategory(userId);
    const planFlags = SubscriptionPlansConfig.getFeatureFlagsByCategory(category, this._getUserType());
    if (typeof planFlags?.clientConnect === 'boolean') return planFlags.clientConnect;

    return (
      category === 'Lawyer-Basic' ||
      category === 'Lawyer-Premium' ||
      category === 'Lawyer-Enterprise'
    );
  }

  /**
   * Check if user is on Free plan
   */
  async isFreePlan(userId) {
    const category = await this._getSubscriptionCategory(userId);
    return category === null;
  }

  /**
   * Check if user is on Basic plan
   */
  async isBasicPlan(userId) {
    const category = await this._getSubscriptionCategory(userId);
    return category === SubscriptionPlansConfig.LAWYER_BASIC.category;
  }

  /**
   * Check if user is on Premium plan
   */
  async isPremiumPlan(userId) {
    const category = await this._getSubscriptionCategory(userId);
    return category === SubscriptionPlansConfig.LAWYER_PREMIUM.category;
  }

  /**
   * Check if user is on Enterprise plan
   */
  async isEnterprisePlan(userId) {
    const category = await this._getSubscriptionCategory(userId);
    return category === SubscriptionPlansConfig.LAWYER_ENTERPRISE.category;
  }

  /**
   * Check if user has any paid subscription
   */
  async hasPaidSubscription(userId) {
    const category = await this._getSubscriptionCategory(userId);
    return category !== null;
  }

  // ==========================================
  // PLAN DETAILS
  // ==========================================

  /**
   * Get user's current plan name for display
   */
  async getPlanDisplayName(userId) {
    const category = await this._getSubscriptionCategory(userId);

    switch (category) {
      case 'Lawyer-Basic':
        return 'Basic Plan';
      case 'Lawyer-Premium':
        return 'Premium Plan';
      case 'Lawyer-Enterprise':
        return 'Enterprise Plan';
      case 'lawStudent':
        return 'Law Student Plan';
      default:
        return 'Free Plan';
    }
  }

  /**
   * Get formatted limits text for display
   */
  async getLimitsText(userId) {
    const caseLimit = await this.getMaxCaseExportLimit(userId);
    const matesLimit = await this.getMaxMatesSharingLimit(userId);

    if (caseLimit === 999999) {
      return 'Unlimited case exports • Unlimited mates';
    }

    return `${caseLimit} case exports • ${matesLimit} mates`;
  }

  // ==========================================
  // UPGRADE REQUIREMENTS
  // ==========================================

  /**
   * Get recommended plan for a specific feature
   */
  getRecommendedPlanForFeature(feature) {
    switch (feature) {
      case 'ai_chatbot':
      case 'client_connect':
        return 'Basic Plan or higher';
      case 'case_export_100':
        return 'Basic Plan';
      case 'case_export_250':
        return 'Premium Plan';
      case 'unlimited_export':
        return 'Enterprise Plan';
      default:
        return 'Premium Plan';
    }
  }

  /**
   * Get upgrade message for case export limit
   */
  async getCaseExportUpgradeMessage(userId, currentCount) {
    const category = await this._getSubscriptionCategory(userId);

    if (category === null) {
      const freeLimit = this._isClientUser() ? SubscriptionPlansConfig.CLIENT_FREE_PLAN_CASE_LIMIT : SubscriptionPlansConfig.FREE_PLAN_CASE_LIMIT;
      return `You have reached the free plan limit of ${freeLimit} case exports.\n\nUpgrade to Premium Plan (₹499/month) for up to 250 cases!`;
    } else if (category === 'Lawyer-Basic') {
      return 'You have reached the Basic plan limit of 100 case exports.\n\nUpgrade to Premium Plan (₹499/month) to export up to 250 cases!';
    } else if (category === 'Lawyer-Premium') {
      return 'You have reached the Premium plan limit of 250 case exports.\n\nContact us for Enterprise Plan to get unlimited case exports!';
    }

    return 'Upgrade your plan to export more cases!';
  }

  /**
   * Get upgrade message for mate sharing limit
   */
  async getMatesSharingUpgradeMessage(userId, currentCount) {
    const category = await this._getSubscriptionCategory(userId);

    if (category === null) {
      return 'You have reached the free plan limit of 10 mates.\n\nUpgrade to Basic Plan (₹249/month) to share with up to 5 mates!';
    } else if (category === 'Lawyer-Basic') {
      return 'You have reached the Basic plan limit of 5 mates.\n\nUpgrade to Premium Plan (₹499/month) to share with up to 10 mates!';
    } else if (category === 'Lawyer-Premium') {
      return 'You have reached the Premium plan limit of 10 mates.\n\nContact us for Enterprise Plan to share with unlimited mates!';
    }

    return 'Upgrade your plan to share with more mates!';
  }

  // ==========================================
  // SUBSCRIPTION-BASED CASE LOCKING
  // ==========================================

  /**
   * Get active subscription details including expiry date
   * @param {string} userId - The user's ID
   * @returns {Promise<Object|null>} Subscription details or null if no active subscription
   */
  async getActiveSubscriptionDetails(userId) {
    try {
      const subscriptionDoc = await getDoc(doc(db, 'subscriptions', userId));

      if (!subscriptionDoc.exists()) {
        return null; // No subscription document
      }

      const data = subscriptionDoc.data();
      if (!data.transactions || data.transactions.length === 0) {
        return null;
      }

      // Find the latest active transaction
      let latestEndDate = null;
      let activeTransaction = null;

      for (const transaction of data.transactions) {
        const endDate = transaction['end-time']?.toDate
          ? transaction['end-time'].toDate()
          : new Date(transaction['end-time']);

        const startDate = transaction['start-time']?.toDate
          ? transaction['start-time'].toDate()
          : new Date(transaction['start-time']);

        // Only consider active subscriptions
        if (endDate > new Date()) {
          if (!latestEndDate || endDate > latestEndDate) {
            latestEndDate = endDate;
            activeTransaction = {
              tier: transaction.category || null,
              startDate: startDate,
              endDate: endDate,
              isActive: true,
              transactionId: transaction.id || null
            };
          }
        }
      }

      return activeTransaction;
    } catch (error) {
      console.error('Error fetching active subscription details:', error);
      return null;
    }
  }

  /**
   * Get subscription metadata to be stored with a new case
   *
   * For FREE plan users: Track cases from index 10+ (cases 11-25 can be locked on expiry)
   * For PAID plan users: Track cases from index 20+ (cases 21+ can be locked on expiry)
   *
   * @param {string} userId - The user's ID
   * @param {number} currentPendingCount - Current number of pending cases
   * @returns {Promise<Object|null>} Subscription metadata or null if not needed
   */
  async getSubscriptionMetadataForNewCase(userId, currentPendingCount) {
    try {
      const subscriptionDetails = await this.getActiveSubscriptionDetails(userId);
      const category = subscriptionDetails?.tier || null;

      // Determine the threshold based on subscription type
      // FREE plan: track from case 11 (index 10) - first 10 always unlocked
      // PAID plan: track from case 21 (index 20) - first 20 always unlocked
      const trackingThreshold = category === null
        ? SubscriptionPlansConfig.FREE_TIER_ALWAYS_UNLOCKED  // 10 for free plan
        : SubscriptionPlansConfig.PAID_TIER_BASELINE_UNLOCKED; // 20 for paid plans

      // No tracking needed for cases within the always-unlocked threshold
      if (currentPendingCount < trackingThreshold) {
        console.log(`📦 No metadata needed - case ${currentPendingCount + 1} within always-unlocked threshold (${trackingThreshold})`);
        return null;
      }

      // For free plan users adding cases 11-25, track with free plan metadata
      // For paid plan users adding cases 21+, track with paid plan metadata

      // If there is NO active subscription (a lapse/gap between plans, or an expired
      // free trial), we STILL write tracking metadata so the case can lock. Returning
      // null here was a leak: cases added during a gap stayed permanently unlocked even
      // after the user re-subscribed. Tracked as a free-plan-style case
      // (addedDuringFreePlan) so it locks while no plan is active and unlocks once an
      // active plan (within the limit) covers it.
      if (!subscriptionDetails && currentPendingCount >= SubscriptionPlansConfig.PAID_TIER_BASELINE_UNLOCKED) {
        console.warn('⚠️ Adding case beyond baseline without active subscription — tracking as free-plan case so it can lock');
      }

      return {
        addedAt: new Date(),
        addedAtSubscriptionTier: category, // null when no active plan, tier name for paid
        pendingCaseCountWhenAdded: currentPendingCount,
        subscriptionExpiry: subscriptionDetails?.endDate || null,
        transactionId: subscriptionDetails?.transactionId || null,
        requiresSubscription: true,
        // Track whether this was added with no active paid plan (free/gap) for lock logic
        addedDuringFreePlan: category === null
      };
    } catch (error) {
      console.error('Error generating subscription metadata:', error);
      return null;
    }
  }

  /**
   * Check if a specific case should be locked based on subscription status
   *
   * Lock Logic:
   * - FREE PLAN users:
   *   - First 10 cases (index 0-9): NEVER locked
   *   - Cases 11-25 (index 10-24): Locked if free plan EXPIRED, unlocked if ACTIVE
   * - PAID PLAN users (existing logic unchanged):
   *   - First 20 cases (index 0-19): NEVER locked (baseline)
   *   - Cases 21+ (index 20+): Locked if paid plan EXPIRED, unlocked if ACTIVE and within tier limit
   *
   * @param {string} userId - The user's ID
   * @param {Object} caseDoc - The case document (with id and data)
   * @param {number} caseIndex - The case's index when sorted by creation date (0-based)
   * @returns {Promise<Object>} Lock status with reason
   */
  async getCaseLockedStatus(userId, caseDoc, caseIndex) {
    try {
      console.log('🔐 [getCaseLockedStatus] Checking lock status:', {
        userId,
        caseId: caseDoc.id,
        caseIndex
      });

      const caseData = caseDoc.data ? caseDoc.data() : caseDoc;
      const metadata = caseData.subscriptionMetadata;

      // Check if user has an active subscription
      const activeSubscription = await this.getActiveSubscriptionDetails(userId);
      const currentTier = activeSubscription?.tier || null;
      const hasActiveSubscription = activeSubscription && activeSubscription.isActive;

      console.log('  🔍 Subscription status:', {
        hasActiveSubscription,
        currentTier,
        hasMetadata: !!metadata
      });

      // Determine the "always unlocked" threshold based on context
      // If case was added during free plan OR user is currently on free plan, use free tier threshold (10)
      // If case was added during paid plan OR user is currently on paid plan, use paid tier threshold (20)
      const wasAddedDuringFreePlan = metadata?.addedDuringFreePlan === true || metadata?.addedAtSubscriptionTier === null;
      const isCurrentlyOnFreePlan = currentTier === null;

      // PAID PLAN LOGIC (existing - unchanged)
      // If case has metadata from a paid plan subscription, use paid tier rules
      if (metadata && metadata.requiresSubscription && !wasAddedDuringFreePlan) {
        // First 20 cases are ALWAYS unlocked for paid plan users (baseline)
        if (caseIndex < SubscriptionPlansConfig.PAID_TIER_BASELINE_UNLOCKED) {
          console.log('  ✅ Case in paid tier baseline (index < 20)');
          return {
            isLocked: false,
            reason: 'paid_tier_baseline'
          };
        }

        // Check if user has active paid subscription
        if (hasActiveSubscription && currentTier !== null) {
          const tierLimit = SubscriptionPlansConfig.getCaseLimitByTier(currentTier, this._isClientUser());
          console.log('  📊 Paid tier limit check:', { tier: currentTier, tierLimit, caseIndex });

          if (caseIndex >= tierLimit) {
            console.log('  🔒 CASE LOCKED - exceeds paid tier limit');
            return {
              isLocked: true,
              reason: 'exceeds_tier_limit',
              currentTier,
              tierLimit,
              caseIndex
            };
          }

          console.log('  ✅ Case within paid tier limit - unlocked');
          return {
            isLocked: false,
            reason: 'active_paid_subscription'
          };
        }

        // Paid subscription expired - lock cases beyond baseline
        console.log('  🔒 CASE LOCKED - paid subscription expired');
        return {
          isLocked: true,
          reason: 'paid_subscription_expired',
          expiredTier: metadata.addedAtSubscriptionTier
        };
      }

      // FREE PLAN LOGIC (new)
      // First 10 cases are ALWAYS unlocked for free plan users
      if (caseIndex < SubscriptionPlansConfig.FREE_TIER_ALWAYS_UNLOCKED) {
        console.log('  ✅ Case in free tier always-unlocked zone (index < 10)');
        return {
          isLocked: false,
          reason: 'free_tier_always_unlocked'
        };
      }

      // Cases 11-25: Check if free plan is still active
      // If no metadata, this is a legacy case - treat as unlocked
      if (!metadata || !metadata.requiresSubscription) {
        console.log('  ⚠️ No subscription metadata - treating as unlocked (legacy case)');
        return {
          isLocked: false,
          reason: 'no_subscription_tracking'
        };
      }

      // Case 11-25 with metadata - check if user has any active subscription (free or paid)
      if (hasActiveSubscription) {
        // User has active subscription (free or paid) - check limits
        const tierLimit = SubscriptionPlansConfig.getCaseLimitByTier(currentTier, this._isClientUser());
        console.log('  📊 Free plan case - tier limit check:', { tier: currentTier || 'free', tierLimit, caseIndex });

        if (caseIndex >= tierLimit) {
          console.log('  🔒 CASE LOCKED - exceeds current tier limit');
          return {
            isLocked: true,
            reason: 'exceeds_tier_limit',
            currentTier: currentTier || 'free',
            tierLimit,
            caseIndex
          };
        }

        console.log('  ✅ Case within tier limit - unlocked');
        return {
          isLocked: false,
          reason: 'active_subscription'
        };
      }

      // No active subscription - free plan expired, lock cases 11-25
      console.log('  🔒 CASE LOCKED - free plan expired (case index >= 10)');
      return {
        isLocked: true,
        reason: 'free_plan_expired',
        expiredTier: 'free'
      };
    } catch (error) {
      console.error('❌ Error checking case lock status:', error);
      // Fail open - don't lock on error
      return {
        isLocked: false,
        reason: 'error',
        error: error.message
      };
    }
  }

  /**
   * Batch check for multiple cases to determine which are locked
   * Returns a map of case ID to lock status
   *
   * @param {string} userId - The user's ID
   * @param {Array} cases - Array of case documents
   * @returns {Promise<Map>} Map of caseId to lock status object
   */
  async getLockedCasesMap(userId, cases) {
    try {
      // Helper function to get timestamp value from various formats
      const getTimeValue = (data, docId) => {
        const addedAt = data.subscriptionMetadata?.addedAt;
        if (addedAt) {
          if (addedAt.toDate) return addedAt.toDate().getTime();
          if (typeof addedAt === 'number') return addedAt;
        }
        const createdAt = data.createdAt;
        if (createdAt) {
          if (createdAt.toDate) return createdAt.toDate().getTime();
          if (typeof createdAt === 'number') return createdAt;
        }
        const timestamp = data.timestamp;
        if (timestamp) {
          if (timestamp.toDate) return timestamp.toDate().getTime();
          if (typeof timestamp === 'number') return timestamp;
        }
        return this.extractTimestampFromDocId(docId);
      };

      // Sort cases by creation date (oldest first) to determine their index
      const sortedCases = [...cases].sort((a, b) => {
        const aData = a.data ? a.data() : a;
        const bData = b.data ? b.data() : b;
        return getTimeValue(aData, a.id) - getTimeValue(bData, b.id);
      });

      // Fetch subscription details ONCE (instead of per-case)
      const activeSubscription = await this.getActiveSubscriptionDetails(userId);
      const currentTier = activeSubscription?.tier || null;
      const hasActiveSubscription = activeSubscription && activeSubscription.isActive;
      const tierLimit = SubscriptionPlansConfig.getCaseLimitByTier(currentTier, this._isClientUser());

      const lockStatusMap = new Map();

      // Check each case's lock status synchronously (no async per-case)
      for (let i = 0; i < sortedCases.length; i++) {
        const caseDoc = sortedCases[i];
        const caseData = caseDoc.data ? caseDoc.data() : caseDoc;
        const metadata = caseData.subscriptionMetadata;
        const wasAddedDuringFreePlan = metadata?.addedDuringFreePlan === true || metadata?.addedAtSubscriptionTier === null;

        let lockStatus;

        // PAID PLAN LOGIC
        if (metadata && metadata.requiresSubscription && !wasAddedDuringFreePlan) {
          if (i < SubscriptionPlansConfig.PAID_TIER_BASELINE_UNLOCKED) {
            lockStatus = { isLocked: false, reason: 'paid_tier_baseline' };
          } else if (hasActiveSubscription && currentTier !== null) {
            lockStatus = i >= tierLimit
              ? { isLocked: true, reason: 'exceeds_tier_limit', currentTier, tierLimit, caseIndex: i }
              : { isLocked: false, reason: 'active_paid_subscription' };
          } else {
            lockStatus = { isLocked: true, reason: 'paid_subscription_expired', expiredTier: metadata.addedAtSubscriptionTier };
          }
        }
        // FREE PLAN LOGIC
        else if (i < SubscriptionPlansConfig.FREE_TIER_ALWAYS_UNLOCKED) {
          lockStatus = { isLocked: false, reason: 'free_tier_always_unlocked' };
        } else if (!metadata || !metadata.requiresSubscription) {
          lockStatus = { isLocked: false, reason: 'no_subscription_tracking' };
        } else if (hasActiveSubscription) {
          lockStatus = i >= tierLimit
            ? { isLocked: true, reason: 'exceeds_tier_limit', currentTier: currentTier || 'free', tierLimit, caseIndex: i }
            : { isLocked: false, reason: 'active_subscription' };
        } else {
          lockStatus = { isLocked: true, reason: 'free_plan_expired', expiredTier: 'free' };
        }

        lockStatusMap.set(caseDoc.id, lockStatus);
      }

      // Summary log only
      const lockedCount = Array.from(lockStatusMap.values()).filter(v => v.isLocked === true).length;
      console.log(`📊 Lock status: ${sortedCases.length} cases | Locked: ${lockedCount} | Unlocked: ${sortedCases.length - lockedCount}`);

      return lockStatusMap;
    } catch (error) {
      console.error('Error generating locked cases map:', error);
      return new Map();
    }
  }

  /**
   * Get count of locked cases for a user
   *
   * @param {string} userId - The user's ID
   * @param {Array} cases - Array of case documents
   * @returns {Promise<number>} Number of locked cases
   */
  async getLockedCasesCount(userId, cases) {
    const lockStatusMap = await this.getLockedCasesMap(userId, cases);
    let lockedCount = 0;

    for (const [, status] of lockStatusMap) {
      if (status.isLocked) {
        lockedCount++;
      }
    }

    return lockedCount;
  }

  // ==========================================
  // TEAM MEMBER LIMITS
  // ==========================================

  /**
   * Get maximum total team members allowed across all teams for a user
   * @param {string} userId - The user's ID
   * @returns {Promise<number>} Maximum total members allowed
   */
  async getMaxTeamMembersLimit(userId) {
    try {
      // Check admin overrides first
      const overrides = await this._getUserOverrides(userId);
      if (overrides && overrides.teamMembersLimit !== undefined) {
        return overrides.teamMembersLimit === -1 ? 999999 : overrides.teamMembersLimit;
      }

      const subscription = await this.getActiveSubscriptionDetails(userId);

      if (subscription && subscription.isActive) {
        // Active subscription - use tier-based limit
        return SubscriptionPlansConfig.getTeamMembersLimitByTier(subscription.tier, true);
      }

      // No active subscription (free expired or no subscription)
      return SubscriptionPlansConfig.getTeamMembersLimitByTier(null, false);
    } catch (error) {
      console.error('Error getting max team members limit:', error);
      return SubscriptionPlansConfig.TEAM_MEMBERS_FREE_EXPIRED; // Fail safe with lowest limit
    }
  }

  /**
   * Get the set of unique member IDs across all teams owned by a user
   * @param {string} userId - The user's ID
   * @returns {Promise<Set<string>>} Set of unique member IDs
   */
  async getUniqueTeamMemberIds(userId) {
    try {
      const teamsQuery = query(
        collection(db, 'teams'),
        where('ownerId', '==', userId)
      );
      const teamsSnapshot = await getDocs(teamsQuery);

      const uniqueMembers = new Set();
      teamsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (Array.isArray(data.members)) {
          data.members.forEach(memberId => uniqueMembers.add(memberId));
        }
      });

      return uniqueMembers;
    } catch (error) {
      console.error('Error getting unique team member IDs:', error);
      return new Set();
    }
  }

  /**
   * Get total member count across all teams owned by a user
   * @param {string} userId - The user's ID
   * @returns {Promise<number>} Total member count across all teams
   */
  async getTotalTeamMembersCount(userId) {
    const uniqueMembers = await this.getUniqueTeamMemberIds(userId);
    return uniqueMembers.size;
  }

  /**
   * Check if user can add more team members
   * @param {string} userId - The user's ID
   * @returns {Promise<Object>} Result with canAdd, currentCount, maxLimit, planName, isExpired, upgradeMessage
   */
  async canAddTeamMember(userId) {
    try {
      const [currentCount, maxLimit, planName, subscription] = await Promise.all([
        this.getTotalTeamMembersCount(userId),
        this.getMaxTeamMembersLimit(userId),
        this.getPlanDisplayName(userId),
        this.getActiveSubscriptionDetails(userId),
      ]);

      const isExpired = !subscription || !subscription.isActive;
      const canAdd = currentCount < maxLimit;

      let upgradeMessage = '';
      if (!canAdd) {
        const category = subscription?.tier || null;
        if (category === null && isExpired) {
          upgradeMessage = `You've reached the limit of ${maxLimit} team members. Your free plan has expired. Subscribe to add more members!`;
        } else if (category === null) {
          upgradeMessage = `You've reached the free plan limit of ${maxLimit} team members. Upgrade to Basic Plan (₹249/month) for up to 5 members!`;
        } else if (category === 'Lawyer-Basic') {
          upgradeMessage = `You've reached the Basic plan limit of ${maxLimit} team members. Upgrade to Premium Plan (₹499/month) for up to 10 members!`;
        } else if (category === 'Lawyer-Premium') {
          upgradeMessage = `You've reached the Premium plan limit of ${maxLimit} team members. Contact us for Enterprise Plan with unlimited members!`;
        } else {
          upgradeMessage = 'Upgrade your plan to add more team members!';
        }
      }

      return {
        canAdd,
        currentCount,
        maxLimit,
        planName,
        isExpired,
        upgradeMessage,
      };
    } catch (error) {
      console.error('Error checking canAddTeamMember:', error);
      // Fail open
      return {
        canAdd: true,
        currentCount: 0,
        maxLimit: 0,
        planName: 'Unknown',
        isExpired: false,
        upgradeMessage: '',
      };
    }
  }

  // ==========================================
  // DEFAULT TEAM MANAGEMENT
  // ==========================================

  /**
   * Get or create the single default team for individual case sharing.
   * All individually-shared mates go into this one team.
   * @param {string} userId - The team owner's ID
   * @returns {Promise<Object>} The default team { ref, id, data }
   */
  async getOrCreateDefaultTeam(userId) {
    try {
      const teamsQuery = query(
        collection(db, 'teams'),
        where('ownerId', '==', userId)
      );
      const snapshot = await getDocs(teamsQuery);

      const existing = snapshot.docs.find(d => d.data().isDefault === true);

      if (existing) {
        return { ref: existing.ref, id: existing.id, data: existing.data() };
      }

      const newTeamRef = await addDoc(collection(db, 'teams'), {
        teamName: 'Case/Cases shared to',
        ownerId: userId,
        isDefault: true,
        members: [],
        createdAt: serverTimestamp(),
      });

      return {
        ref: newTeamRef,
        id: newTeamRef.id,
        data: { teamName: 'Case/Cases shared to', ownerId: userId, isDefault: true, members: [] },
      };
    } catch (error) {
      console.error('Error getting/creating default team:', error);
      throw error;
    }
  }

  /**
   * Add a member to the default team (creates team if needed)
   * @param {string} userId - The team owner's ID
   * @param {string} memberUid - The member to add
   */
  async addMemberToDefaultTeam(userId, memberUid) {
    try {
      const { ref } = await this.getOrCreateDefaultTeam(userId);
      await updateDoc(ref, {
        members: arrayUnion(memberUid),
      });
    } catch (error) {
      console.error('Error adding member to default team:', error);
    }
  }

  /**
   * Remove a member from the default team (does nothing if team doesn't exist)
   * @param {string} userId - The team owner's ID
   * @param {string} memberUid - The member to remove
   */
  async removeMemberFromDefaultTeam(userId, memberUid) {
    try {
      const teamsQuery = query(
        collection(db, 'teams'),
        where('ownerId', '==', userId)
      );
      const snapshot = await getDocs(teamsQuery);

      const existing = snapshot.docs.find(d => d.data().isDefault === true);
      if (!existing) return;

      await updateDoc(existing.ref, {
        members: arrayRemove(memberUid),
      });
    } catch (error) {
      console.error('Error removing member from default team:', error);
    }
  }

  /**
   * Get all cases that the owner has shared with a specific member
   * @param {string} ownerUserId - The case owner's ID
   * @param {string} memberUid - The member to check
   * @returns {Promise<Array>} Array of case objects
   */
  async getCasesSharedWithMember(ownerUserId, memberUid) {
    try {
      const cases = [];

      // Query pending cases
      const pendingQuery = query(
        collection(db, 'pending'),
        where('owner', '==', ownerUserId),
        where('team', 'array-contains', memberUid)
      );
      const pendingSnap = await getDocs(pendingQuery);
      pendingSnap.forEach(d => {
        cases.push({ id: d.id, collection: 'pending', ...d.data() });
      });

      // Query disposed cases
      const disposedQuery = query(
        collection(db, 'disposed'),
        where('owner', '==', ownerUserId),
        where('team', 'array-contains', memberUid)
      );
      const disposedSnap = await getDocs(disposedQuery);
      disposedSnap.forEach(d => {
        cases.push({ id: d.id, collection: 'disposed', ...d.data() });
      });

      return cases;
    } catch (error) {
      console.error('Error getting cases shared with member:', error);
      return [];
    }
  }

  // ==========================================
  // CENTRALIZED CASE LIMIT CHECKING
  // ==========================================

  /**
   * Check if user can export the specified number of cases
   * This method queries Firebase for current pending and disposed cases
   * and checks against subscription limits
   *
   * Note: Pending and disposed cases are counted separately, each with their own limits
   *
   * @param {string} userId - The user's ID
   * @param {number} numberOfCasesToExport - Number of cases user wants to export
   * @returns {Promise<Object>} Result object with:
   *   - canProceed: boolean indicating if export can proceed
   *   - pendingCount: current number of pending cases
   *   - disposedCount: current number of disposed cases
   *   - pendingLimit: maximum allowed pending cases
   *   - disposedLimit: maximum allowed disposed cases
   *   - planName: user's plan display name
   *   - wouldExceedPending: boolean indicating if pending limit would be exceeded
   */
  async checkCaseLimitBeforeExport(userId, numberOfCasesToExport = 1, targetCollection = 'pending') {
    console.log('🔍 [FeatureGatingService] Checking case limit...', {
      numberOfCasesToExport,
      targetCollection,
      userId
    });

    if (!userId) {
      console.log('❌ [FeatureGatingService] No user ID provided');
      return {
        canProceed: false,
        pendingCount: 0,
        disposedCount: 0,
        pendingLimit: 0,
        disposedLimit: 0,
        planName: 'Unknown',
        wouldExceedPending: false,
        wouldExceedDisposed: false,
        error: 'No user ID provided'
      };
    }

    try {
      // Get current case count from Firebase (pending and disposed separately)
      const pendingQuery = query(
        collection(db, "pending"),
        where("owner", "==", userId)
      );
      const pendingSnapshot = await getDocs(pendingQuery);

      const disposedQuery = query(
        collection(db, "disposed"),
        where("owner", "==", userId)
      );
      const disposedSnapshot = await getDocs(disposedQuery);

      const pendingCount = pendingSnapshot.size;
      const disposedCount = disposedSnapshot.size;
      const pendingLimit = await this.getMaxCaseExportLimit(userId);
      const disposedLimit = await this.getMaxDisposedCaseLimit(userId);
      const planName = await this.getPlanDisplayName(userId);

      // Check limits based on target collection
      const wouldExceedPending = pendingCount + numberOfCasesToExport > pendingLimit;
      const wouldExceedDisposed = disposedCount + numberOfCasesToExport > disposedLimit;

      // Determine if can proceed based on target collection
      const wouldExceed = targetCollection === 'disposed' ? wouldExceedDisposed : wouldExceedPending;
      const currentCount = targetCollection === 'disposed' ? disposedCount : pendingCount;
      const maxLimit = targetCollection === 'disposed' ? disposedLimit : pendingLimit;

      console.log('📊 [FeatureGatingService] Case limit check:', {
        pendingCount,
        disposedCount,
        numberOfCasesToExport,
        targetCollection,
        pendingLimit,
        disposedLimit,
        planName,
        wouldExceedPending,
        wouldExceedDisposed
      });

      return {
        canProceed: !wouldExceed,
        pendingCount,
        disposedCount,
        pendingLimit,
        disposedLimit,
        planName,
        wouldExceedPending,
        wouldExceedDisposed,
        currentCount,
        maxLimit,
        wouldExceed
      };
    } catch (error) {
      console.error('❌ [FeatureGatingService] Error checking case limit:', error);
      // Fail open - allow export on error to avoid blocking users
      return {
        canProceed: true,
        pendingCount: 0,
        disposedCount: 0,
        pendingLimit: 0,
        disposedLimit: 0,
        planName: 'Unknown',
        wouldExceedPending: false,
        wouldExceedDisposed: false,
        currentCount: 0,
        maxLimit: 0,
        wouldExceed: false,
        error: error.message
      };
    }
  }
}

// Export singleton instance
const featureGatingService = new FeatureGatingService();
export default featureGatingService;
